<!--
DashboardRecentViews

Operational dashboard card for team leaders and team members. It surfaces
recently opened hosted views, recently uploaded views, and the inline upload
tile when upload permission exists.

Permission rules:
- summary.permissions.canUpload decides whether the upload tile renders.
- dashboardRole differentiates leader and member copy only.

@author Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
-->
<template>
    <v-card class="workspace-dashboard__recent-card" elevation="0" data-home-tour-recent-views>
        <div class="workspace-dashboard__section-head">
            <div>
                <div class="workspace-dashboard__section-kicker">{{ recentViewsKicker }}</div>
                <h2>{{ recentViewsTitle }}</h2>
            </div>
            <v-btn variant="text" :href="summary.links?.viewer || '/view'">
                View all
            </v-btn>
        </div>

        <div v-if="hasRecentViews" class="workspace-dashboard__recent-views-grid">
            <article
                v-for="(view, index) in displayRecentViews"
                :key="view.viewUuid"
                class="workspace-dashboard__recent-view-card"
            >
                <recent-views-card
                    :view="view"
                    :image-url="getImageUrl(index)"
                    :favourite="false"
                    :disable="favViewsUuids.includes(view.viewUuid)"
                    @add-to-favourites="addToFavourites"
                />
            </article>

            <a
                v-if="permissions.canUpload"
                class="workspace-dashboard__dataset-upload-tile"
                :href="summary.links?.upload || '/upload'"
            >
                <span class="workspace-dashboard__dataset-upload-icon">
                    <v-icon size="32">$vuetify.icons.upload</v-icon>
                </span>
                <strong>Upload New Dataset</strong>
                <p>Drag &amp; drop your dataset files here or click to browse</p>
                <span>Supported formats</span>
                <div class="workspace-dashboard__upload-platforms workspace-dashboard__upload-platforms--compact">
                    <span>DJI</span>
                    <span>Agisoft</span>
                    <span>Pix4D</span>
                    <span>RealityCapture</span>
                    <span>More</span>
                </div>
            </a>
        </div>
        <div v-else class="workspace-dashboard__empty-state workspace-dashboard__empty-state--recent-views">
            <p>{{ recentViewsEmptyCopy }}</p>
            <a
                v-if="permissions.canUpload"
                class="workspace-dashboard__dataset-upload-tile"
                :href="summary.links?.upload || '/upload'"
            >
                <span class="workspace-dashboard__dataset-upload-icon">
                    <v-icon size="32">$vuetify.icons.upload</v-icon>
                </span>
                <strong>Upload New Dataset</strong>
                <p>Drag &amp; drop your dataset files here or click to browse</p>
                <span>Supported formats</span>
                <div class="workspace-dashboard__upload-platforms workspace-dashboard__upload-platforms--compact">
                    <span>DJI</span>
                    <span>Agisoft</span>
                    <span>Pix4D</span>
                    <span>RealityCapture</span>
                    <span>More</span>
                </div>
            </a>
        </div>

        <div class="workspace-dashboard__recent-subsection">
            <div class="workspace-dashboard__section-head workspace-dashboard__section-head--compact">
                <div>
                    <div class="workspace-dashboard__section-kicker">Recently uploaded</div>
                    <h3>Recently Uploaded Views</h3>
                </div>
                <v-btn
                    v-if="permissions.canUpload"
                    variant="text"
                    :href="summary.links?.upload || '/upload'"
                >
                    Upload
                </v-btn>
            </div>

            <div v-if="showRecentlyUploadedSection && recentlyUploaded.length" class="workspace-dashboard__recent-views-grid">
                <article
                    v-for="(view, index) in displayRecentlyUploaded"
                    :key="view.viewUuid"
                    class="workspace-dashboard__recent-view-card"
                >
                    <recent-views-card
                        :view="view"
                        :image-url="getUploadedImageUrl(index)"
                        :favourite="false"
                        :disable="favViewsUuids.includes(view.viewUuid)"
                        @add-to-favourites="addToFavourites"
                    />
                </article>
            </div>
            <div v-else-if="showRecentlyUploadedSection" class="workspace-dashboard__empty-state">
                Your recently uploaded views will appear here once they are published.
            </div>
        </div>
    </v-card>
</template>

<script>
import RecentViewsCard from 'components/Dashboard/ViewCard.vue';
import { find, map } from 'lodash';

export default {
    name: 'DashboardRecentViews',
    components: {
        RecentViewsCard,
    },
    props: {
        summary: {
            type: Object,
            default: () => ({}),
        },
        recentViews: {
            type: Array,
            default: () => [],
        },
        recentlyUploaded: {
            type: Array,
            default: () => [],
        },
        favViews: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            recentImageSeeds: [],
            uploadedImageSeeds: [],
        };
    },
    computed: {
        dashboardRole() {
            return this.summary.dashboardRole || 'none';
        },
        permissions() {
            return this.summary.permissions || {};
        },
        hasRecentViews() {
            return this.recentViews.length > 0;
        },
        recentViewsKicker() {
            return this.dashboardRole === 'team_leader'
                ? 'Views you manage'
                : 'Views you can access';
        },
        recentViewsTitle() {
            return 'Recent Views';
        },
        recentViewsEmptyCopy() {
            return this.dashboardRole === 'team_leader'
                ? 'You do not have any recent views yet. As your teams open work, those views will appear here.'
                : 'You do not have any recent views yet. Open shared work in the viewer to start building this list.';
        },
        displayRecentViews() {
            return this.recentViews.slice(0, 4);
        },
        displayRecentlyUploaded() {
            return this.recentlyUploaded.slice(0, 4);
        },
        showRecentlyUploadedSection() {
            return Boolean(this.permissions.canUpload);
        },
        favViewsUuids() {
            return map(this.favViews, 'viewUuid');
        },
    },
    mounted() {
        this.recentImageSeeds = this.buildImageSeeds(this.displayRecentViews.length);
        this.uploadedImageSeeds = this.buildImageSeeds(this.displayRecentlyUploaded.length);
    },
    methods: {
        buildImageSeeds(count) {
            const seeds = [];
            let number;
            for (let index = 0; index < count; index++) {
                do {
                    number = Math.floor(Math.random() * 11) + 1;
                } while (seeds[index - 1] === number || seeds[index + 1] === number);
                seeds.push(number);
            }
            return seeds;
        },
        getImageUrl(index) {
            return `static/img/stock_landscapes/stock${this.recentImageSeeds[index] || 1}.jpg`;
        },
        getUploadedImageUrl(index) {
            return `static/img/stock_landscapes/stock${this.uploadedImageSeeds[index] || 1}.jpg`;
        },
        addToFavourites(viewUuid) {
            this.$emit('add-to-favourites', find(this.recentViews, { viewUuid }));
        },
    },
};
</script>

<style scoped>
.workspace-dashboard__recent-views-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 20px;
}

.workspace-dashboard__recent-view-card {
    min-width: 0;
}

.workspace-dashboard__recent-subsection {
    margin-top: 22px;
}
</style>
